"use client";
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt, faUser, faChevronDown, faCheck } from "@fortawesome/free-solid-svg-icons";
import Button from "../Button/Button";
import styles from "./TopBar.module.css";
import SearchBar from "../../../shared-modules/SearchBar";
import { useNavigate } from "react-router-dom";
import { performLogout } from "../../../../utils/logout";
import { getOrganizationList } from "../../../../services/admin/organization";
const TopBar = ({
  showPortalButton = true,
  showAccountSwitcher = true,
  showLogout = true,
  onAccountSwitch,
  onPortalClick = () => {
    window.location.href = "/dashboard";
  },
  onLogout,
  className = "",
  onSearchChange,
  searchPlaceholder = "Search Device By Imei",
  searchMaxWidth = "320px",
  debounceDelay = 500, 
}) => {
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [adminAccount, setAdminAccount] = useState(null);
  const [subAccounts, setSubAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const debounceTimer = useRef(null);
  const lastEmitted = useRef("");
  const topBarClasses = [styles.topBar, className].filter(Boolean).join(" ");
  const rawLocalUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  })();
  const isSuperUser =
    Array.isArray(rawLocalUser?.account_roles) &&
    rawLocalUser.account_roles.some((r) => r?.name === "Super User");
  const hasExternalRole = Boolean(rawLocalUser?.external_role?.name);
  useEffect(() => {
    const fetchAccounts = async () => {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) {
        setLoading(false);
        return;
      }
      const adminAcc = {
        id: user.id,
        name: user.first_name || "Admin",
        last: user.last_name || "Admin",
        role: user.role || "Admin",
        type: "admin",
      };
      setAdminAccount(adminAcc);
      try {
        const orgList = await getOrganizationList(user.roles || []);
        const mappedOrgs = (orgList || []).map((org) => ({
          id: org.id,
          name: org.name || "Organization",
          role: "Organization",
          type: "sub",
        }));
        setSubAccounts(mappedOrgs);
        const storedAcc = localStorage.getItem("inventory_active_account");
        if (storedAcc) {
          try {
            const parsedAcc = JSON.parse(storedAcc);
            setSelectedAccount(parsedAcc);
          } catch {
            setSelectedAccount(adminAcc);
          }
        } else {
          setSelectedAccount(adminAcc);
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
        setSelectedAccount(adminAcc);
      } finally {
        setLoading(false);
      }
    };
    fetchAccounts();
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsAccountDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleAccountSwitch = (account) => {
    setIsAccountDropdownOpen(false);
    setSelectedAccount(account);
    localStorage.setItem("inventory_active_account", JSON.stringify(account));
    if (onAccountSwitch) onAccountSwitch(account);
  };
  const handleLogout = () => {
    performLogout();
  };
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    const trimmed = (searchValue || "").trim();
    if (!onSearchChange) {
      return;
    }
    if (trimmed === "") {
      if (lastEmitted.current !== "") {
        lastEmitted.current = "";
        onSearchChange("");
      }
      return;
    }
    debounceTimer.current = setTimeout(() => {
      if (lastEmitted.current !== trimmed) {
        lastEmitted.current = trimmed;
        onSearchChange(trimmed);
      }
    }, debounceDelay);
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, [searchValue, debounceDelay, onSearchChange]);
  const handleSearchInput = (val) => {
    setSearchValue(val ?? "");
  };
  return (
    <header className={topBarClasses}>
      <div className={styles.container}>
        <div className={styles.leftSection}>
          <SearchBar
            value={searchValue}
            onSearchChange={handleSearchInput}
            placeholder={searchPlaceholder}
            width={searchMaxWidth}
          />
        </div>
        <div className={styles.rightSection}>
          {showPortalButton && (
            <Button variant="outline" size="small" onClick={onPortalClick} className={styles.portalButton}>
              Go to Portal
            </Button>
          )}
          {showAccountSwitcher && hasExternalRole && (
            <div className={styles.accountSwitcher} ref={dropdownRef}>
              <button
                className={styles.accountButton}
                onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
                aria-expanded={isAccountDropdownOpen}
                disabled={loading}
              >
                <div className={styles.userIcon}>
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <span className={styles.accountText}>
                  {loading
                    ? "Loading..."
                    : selectedAccount?.role === "Admin"
                    ? "Warehouse"
                    : `${selectedAccount?.name || ""} ${selectedAccount?.last || ""}`}
                </span>
                <div className={styles.dropdownArrow}>
                  <FontAwesomeIcon icon={faChevronDown} className={isAccountDropdownOpen ? styles.rotated : ""} />
                </div>
              </button>
              {isAccountDropdownOpen && !loading && (
                <div className={styles.accountDropdown}>
                  <div className={styles.dropdownHeader}>
                    <span className={styles.dropdownTitle}>Switch Account</span>
                  </div>
                  <div className={styles.accountList}>
                    {adminAccount && (
                      <button
                        key={adminAccount.id}
                        className={`${styles.accountItem} ${selectedAccount?.id === adminAccount.id ? styles.activeAccount : ""}`}
                        onClick={() => handleAccountSwitch(adminAccount)}
                      >
                        <div className={styles.accountAvatar}>W</div>
                        <div className={styles.accountInfo}>
                          <div className={styles.accountName}>Warehouse</div>
                        </div>
                        {selectedAccount?.id === adminAccount.id && (
                          <div className={styles.activeIndicator}>
                            <FontAwesomeIcon icon={faCheck} />
                          </div>
                        )}
                      </button>
                    )}
                    {subAccounts.map((acc) => (
                      <button
                        key={acc.id}
                        className={`${styles.accountItem} ${selectedAccount?.id === acc.id ? styles.activeAccount : ""}`}
                        onClick={() => handleAccountSwitch(acc)}
                      >
                        <div className={styles.accountAvatar}>
                          {acc?.name ? acc.name.charAt(0) : "O"}
                        </div>
                        <div className={styles.accountInfo}>
                          <div className={styles.accountName}>{acc?.name || "Organization"}</div>
                        </div>
                        {selectedAccount?.id === acc.id && (
                          <div className={styles.activeIndicator}>
                            <FontAwesomeIcon icon={faCheck} />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {showLogout && (
            <button className={styles.logoutButton} onClick={handleLogout} title="Logout">
              <div className={styles.logoutIcon}>
                <FontAwesomeIcon icon={faSignOutAlt} />
              </div>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
export default TopBar;